import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

import { FieldError } from "@/components/ui/field-error";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useStockCosting } from "@/hooks/use-stock-costing";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { Plus, Trash2, Package, ChevronDown, AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

// Mock stock items data with cost prices
const mockStockItems = [
  { code: "STK001", description: "Laptop Dell XPS 15", costPrice: 1050.00 },
  { code: "STK002", description: "Office Chair Ergonomic", costPrice: 280.00 },
  { code: "STK003", description: "A4 Paper Ream (500 sheets)", costPrice: 3.50 },
  { code: "STK004", description: "Steel Rods 10mm x 6m", costPrice: 35.00 },
  { code: "STK005", description: "Wireless Mouse Logitech", costPrice: 22.00 },
  { code: "STK006", description: "Standing Desk Adjustable", costPrice: 450.00 },
  { code: "STK007", description: "Ballpoint Pens Blue (Box of 50)", costPrice: 9.50 },
  { code: "STK008", description: "Aluminum Sheets 2mm", costPrice: 65.00 },
  { code: "STK009", description: "Monitor 27\" 4K Dell", costPrice: 350.00 },
  { code: "STK010", description: "Filing Cabinet 4-Drawer", costPrice: 150.00 },
];

const locations = ["Warehouse A", "Warehouse B", "Retail Store", "Distribution Center"];

const writeOffReasons = [
  { value: "damaged", label: "Damaged" },
  { value: "expired", label: "Expired" },
  { value: "obsolete", label: "Obsolete" },
  { value: "theft_loss", label: "Theft/Loss" },
  { value: "quality_issue", label: "Quality Issue" },
  { value: "other", label: "Other" },
];

interface LineItem {
  id: string;
  stockCode: string;
  stockDescription: string;
  location: string;
  quantity: string;
  unitPrice: string;
  lineTotal: number;
  errors: {
    stockCode?: string;
    location?: string;
    quantity?: string;
    unitPrice?: string;
  };
}

interface FormData {
  referenceNumber: string;
  movementDate: Date;
  writeOffReason: string;
  otherReasonDetails: string;
  notes: string;
}

interface FormErrors {
  referenceNumber?: string;
  movementDate?: string;
  writeOffReason?: string;
  otherReasonDetails?: string;
  notes?: string;
}

export function WriteOffMovementPage() {
  const { navigateTo } = useNavigation();
  const { processSale, valuationMethod, getValuationMethodDisplayName } = useStockCosting();
  const [formData, setFormData] = useState<FormData>({
    referenceNumber: "",
    movementDate: new Date(),
    writeOffReason: "",
    otherReasonDetails: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: "1",
      stockCode: "",
      stockDescription: "",
      location: "",
      quantity: "",
      unitPrice: "",
      lineTotal: 0,
      errors: {},
    },
  ]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showHighValueDialog, setShowHighValueDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const referenceInputRef = useRef<HTMLInputElement>(null);

  // Focus on reference number field on mount
  useEffect(() => {
    referenceInputRef.current?.focus();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    const hasData =
      formData.referenceNumber ||
      formData.writeOffReason ||
      formData.otherReasonDetails ||
      formData.notes ||
      lineItems.some(
        (item) => item.stockCode || item.quantity || item.unitPrice || item.location
      );
    setHasUnsavedChanges(hasData);
  }, [formData, lineItems]);



  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate line total
  const calculateLineTotal = (quantity: string, unitPrice: string): number => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  // Calculate summary totals
  const calculateSummary = () => {
    const totalItems = lineItems.filter(item => item.stockCode).length;
    const totalQuantity = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.quantity) || 0),
      0
    );
    const totalValue = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);
    return { totalItems, totalQuantity, totalValue };
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    // Validate reference number
    if (!formData.referenceNumber.trim()) {
      errors.referenceNumber = "Reference number is required";
      isValid = false;
    }

    // Validate movement date
    if (!formData.movementDate) {
      errors.movementDate = "Movement date is required";
      isValid = false;
    } else if (formData.movementDate > new Date()) {
      errors.movementDate = "Movement date cannot be in the future";
      isValid = false;
    }

    // Validate write-off reason
    if (!formData.writeOffReason) {
      errors.writeOffReason = "Write-off reason is required";
      isValid = false;
    }

    // Validate "Other" reason details
    if (formData.writeOffReason === "other") {
      if (!formData.otherReasonDetails.trim()) {
        errors.otherReasonDetails = "Please specify the reason for write-off";
        isValid = false;
      } else if (formData.otherReasonDetails.trim().length < 10) {
        errors.otherReasonDetails = "Reason must be at least 10 characters";
        isValid = false;
      }
    }

    // Validate notes length
    if (formData.notes.length > 500) {
      errors.notes = "Notes must be 500 characters or less";
      isValid = false;
    }

    setFormErrors(errors);

    // Validate line items
    const updatedLineItems = lineItems.map((item) => {
      const itemErrors: LineItem["errors"] = {};

      if (!item.stockCode) {
        itemErrors.stockCode = "Stock item is required";
        isValid = false;
      }

      if (!item.location) {
        itemErrors.location = "Location is required";
        isValid = false;
      }

      const qty = parseFloat(item.quantity);
      if (!item.quantity || isNaN(qty) || qty <= 0) {
        itemErrors.quantity = "Quantity must be greater than 0";
        isValid = false;
      }

      const price = parseFloat(item.unitPrice);
      if (!item.unitPrice || isNaN(price) || price <= 0) {
        itemErrors.unitPrice = "Unit price must be greater than 0";
        isValid = false;
      }

      return { ...item, errors: itemErrors };
    });

    setLineItems(updatedLineItems);

    // Check for at least one valid line item
    const hasValidLineItem = lineItems.some(item => item.stockCode);
    if (!hasValidLineItem) {
      toast.error("At least one line item is required");
      isValid = false;
    }

    // Check for duplicate item-location combinations
    const itemLocationPairs = new Set<string>();
    for (const item of lineItems) {
      if (item.stockCode && item.location) {
        const pair = `${item.stockCode}-${item.location}`;
        if (itemLocationPairs.has(pair)) {
          toast.error("Duplicate item-location combination found");
          isValid = false;
          break;
        }
        itemLocationPairs.add(pair);
      }
    }

    return isValid;
  };

  // Handle form field changes
  const handleFieldChange = (field: keyof FormData, value: string | Date) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    
    // Clear "Other" reason details if reason changes from "other"
    if (field === "writeOffReason" && value !== "other") {
      setFormData((prev) => ({ ...prev, otherReasonDetails: "" }));
      setFormErrors((prev) => ({ ...prev, otherReasonDetails: undefined }));
    }
  };

  // Handle line item changes
  const handleLineItemChange = (
    id: string,
    field: keyof LineItem,
    value: string
  ) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const updatedItem = { ...item, [field]: value };

        // Auto-populate unit price when stock item is selected (use cost price)
        if (field === "stockCode") {
          const stockItem = mockStockItems.find((s) => s.code === value);
          if (stockItem) {
            updatedItem.stockDescription = stockItem.description;
            updatedItem.unitPrice = stockItem.costPrice.toFixed(2);
          }
        }

        // Recalculate line total
        if (field === "quantity" || field === "unitPrice") {
          updatedItem.lineTotal = calculateLineTotal(
            updatedItem.quantity,
            updatedItem.unitPrice
          );
        }

        // Clear error for this field
        if (updatedItem.errors[field as keyof LineItem["errors"]]) {
          updatedItem.errors = {
            ...updatedItem.errors,
            [field]: undefined,
          };
        }

        return updatedItem;
      })
    );
  };

  // Add new line item
  const handleAddLineItem = () => {
    if (lineItems.length >= 50) {
      toast.error("Maximum 50 items per movement");
      return;
    }

    const newItem: LineItem = {
      id: Date.now().toString(),
      stockCode: "",
      stockDescription: "",
      location: "",
      quantity: "",
      unitPrice: "",
      lineTotal: 0,
      errors: {},
    };

    setLineItems((prev) => [...prev, newItem]);

    // Focus on the new row's stock code select after a brief delay
    setTimeout(() => {
      const newRow = document.querySelector(`[data-line-id="${newItem.id}"]`);
      const selectTrigger = newRow?.querySelector('[role="combobox"]') as HTMLElement;
      selectTrigger?.focus();
    }, 100);
  };

  // Remove line item
  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length === 1) {
      // Reset the last line instead of removing it
      setLineItems([{
        id: Date.now().toString(),
        stockCode: "",
        stockDescription: "",
        location: "",
        quantity: "",
        unitPrice: "",
        lineTotal: 0,
        errors: {},
      }]);
      return;
    }

    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      navigateTo("stock-movements");
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('[aria-invalid="true"]');
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      (firstError as HTMLElement)?.focus();
      
      // Count errors
      const errorCount = 
        Object.keys(formErrors).length + 
        lineItems.reduce((sum, item) => sum + Object.keys(item.errors).length, 0);
      
      toast.error(`Please fix ${errorCount} error${errorCount > 1 ? 's' : ''} before saving`);
      return;
    }

    const summary = calculateSummary();

    // Check if high-value write-off (> £1,000)
    if (summary.totalValue > 1000) {
      setShowHighValueDialog(true);
      return;
    }

    await performSave();
  };

  // Perform the actual save operation
  const performSave = async () => {
    setIsSaving(true);
    setShowHighValueDialog(false);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    console.log(`❌ Processing write-off using ${getValuationMethodDisplayName()}`);

    // Process each line item with FIFO or Average Cost
    const movements = lineItems
      .filter(item => item.stockCode)
      .map((item) => {
        // Find the stock item from mock data
        const stockItem = mockStockItems.find(s => s.code === item.stockCode);
        
        if (stockItem && valuationMethod === 'FIFO') {
          // For FIFO, calculate the cost using FIFO logic (consume oldest layers)
          const saleResult = processSale(
            {
              code: item.stockCode,
              description: item.stockDescription,
              quantityOnHand: 100, // Would come from database in real app
              averageCost: stockItem.costPrice,
            },
            parseFloat(item.quantity),
            0, // No selling price for write-offs
            item.location
          );

          console.log(`  ✅ FIFO Write-off: ${item.stockDescription}`);
          console.log(`     Location: ${item.location}, Qty: ${item.quantity}`);
          console.log(`     COGS: £${saleResult.cogs.toFixed(2)} (from ${saleResult.layersConsumed} layer(s))`);
          
          if (saleResult.layersConsumed > 0) {
            console.log(`     📊 Consumed from ${saleResult.layersConsumed} cost layer(s):`);
            // In a real app, we'd show which specific layers were consumed
          }
        } else {
          console.log(`  ✅ Average Cost Write-off: ${item.stockDescription} - Qty: ${item.quantity}, Cost: £${item.unitPrice}`);
        }

        return {
        id: `MOV${Date.now()}-${item.id}`,
        reference: formData.referenceNumber,
        date: formData.movementDate,
        type: "Write-off",
        source: "Manual",
        stockCode: item.stockCode,
        stockDescription: item.stockDescription,
        location: item.location,
        quantity: -parseFloat(item.quantity), // Negative for write-off
        unitPrice: parseFloat(item.unitPrice),
        lineTotal: -item.lineTotal, // Negative for write-off
        userName: "John Smith",
        notes: formData.notes || null,
        writeOffReason: formData.writeOffReason,
        otherReasonDetails: formData.writeOffReason === "other" ? formData.otherReasonDetails : null,
      };
      });

    console.log("Created write-off movements:", movements);

    setIsSaving(false);
    const itemCount = movements.length;
    toast.success(`Write-off recorded successfully - ${itemCount} item${itemCount > 1 ? 's' : ''} removed from inventory`);
    navigateTo("stock-movements");
  };

  const summary = calculateSummary();

  return (
    <div className="space-y-6 pb-24">
      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Write-off Stock</h1>
          <Badge variant="destructive" className="bg-red-500 hover:bg-red-600">
            Write-off
          </Badge>
          <Badge 
            variant="outline" 
            className={valuationMethod === 'FIFO' 
              ? "bg-purple-50 text-purple-700 border-purple-200" 
              : "bg-green-50 text-green-700 border-green-200"}
          >
            {getValuationMethodDisplayName()}
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Remove damaged, expired, or obsolete stock from inventory
        </p>
      </div>

      {/* Valuation Method Info Alert */}
      {valuationMethod === 'fifo' && (
        <Alert className="bg-purple-50 border-purple-200">
          <Info className="h-4 w-4 text-purple-600" />
          <AlertDescription className="text-purple-900">
            <strong>FIFO Mode Active:</strong> Write-offs consume cost layers using FIFO logic. 
            The oldest cost layers are removed first, accurately reflecting the cost of written-off inventory.
          </AlertDescription>
        </Alert>
      )}

      {/* Movement Header Section */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="referenceNumber">
              Reference Number <span className="text-destructive">*</span>
            </Label>
            <Input
              ref={referenceInputRef}
              id="referenceNumber"
              type="text"
              placeholder="e.g., WO-2024-001"
              maxLength={50}
              value={formData.referenceNumber}
              onChange={(e) => handleFieldChange("referenceNumber", e.target.value)}
              aria-required="true"
              aria-invalid={!!formErrors.referenceNumber}
              aria-describedby={formErrors.referenceNumber ? "referenceNumber-error" : "referenceNumber-help"}
              className={cn(formErrors.referenceNumber && "border-destructive")}
            />
            <p id="referenceNumber-help" className="text-sm text-muted-foreground">
              Write-off reference
            </p>
            {formErrors.referenceNumber && (
              <FieldError id="referenceNumber-error" message={formErrors.referenceNumber} />
            )}
          </div>

          {/* Movement Date */}
          <div className="space-y-2">
            <Label htmlFor="movementDate">
              Movement Date <span className="text-destructive">*</span>
            </Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="movementDate"
                  variant="outline"
                  className={cn(
                    "w-full justify-between font-normal",
                    !formData.movementDate && "text-muted-foreground",
                    formErrors.movementDate && "border-destructive"
                  )}
                  aria-required="true"
                  aria-invalid={!!formErrors.movementDate}
                  aria-describedby={formErrors.movementDate ? "movementDate-error" : undefined}
                >
                  {formData.movementDate
                    ? new Intl.DateTimeFormat("en-GB").format(formData.movementDate)
                    : "Select date"}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.movementDate}
                  onSelect={(date) => {
                    if (date) {
                      handleFieldChange("movementDate", date);
                      setDatePickerOpen(false);
                    }
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {formErrors.movementDate && (
              <FieldError id="movementDate-error" message={formErrors.movementDate} />
            )}
          </div>

          {/* Write-off Reason */}
          <div className="space-y-2">
            <Label htmlFor="writeOffReason">
              Write-off Reason <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.writeOffReason}
              onValueChange={(value) => handleFieldChange("writeOffReason", value)}
            >
              <SelectTrigger
                id="writeOffReason"
                className={cn(
                  "w-full",
                  formErrors.writeOffReason && "border-destructive"
                )}
                aria-required="true"
                aria-invalid={!!formErrors.writeOffReason}
                aria-describedby={formErrors.writeOffReason ? "writeOffReason-error" : undefined}
              >
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {writeOffReasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors.writeOffReason && (
              <FieldError id="writeOffReason-error" message={formErrors.writeOffReason} />
            )}
          </div>

          {/* Other Reason Details (Conditional) */}
          {formData.writeOffReason === "other" && (
            <div className="space-y-2">
              <Label htmlFor="otherReasonDetails">
                Please specify reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="otherReasonDetails"
                placeholder="Describe the reason for write-off"
                rows={3}
                value={formData.otherReasonDetails}
                onChange={(e) => handleFieldChange("otherReasonDetails", e.target.value)}
                aria-required="true"
                aria-invalid={!!formErrors.otherReasonDetails}
                aria-describedby={formErrors.otherReasonDetails ? "otherReasonDetails-error" : "otherReasonDetails-help"}
                className={cn(formErrors.otherReasonDetails && "border-destructive")}
              />
              <p id="otherReasonDetails-help" className="text-sm text-muted-foreground">
                Minimum 10 characters required
              </p>
              {formErrors.otherReasonDetails && (
                <FieldError id="otherReasonDetails-error" message={formErrors.otherReasonDetails} />
              )}
            </div>
          )}

          {/* Notes */}
          <div className={cn("space-y-2", formData.writeOffReason !== "other" && "md:col-span-2")}>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional details about the write-off"
              maxLength={500}
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFieldChange("notes", e.target.value)}
              aria-invalid={!!formErrors.notes}
              aria-describedby={formErrors.notes ? "notes-error" : "notes-count"}
              className={cn(formErrors.notes && "border-destructive")}
            />
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Provide context for future reference
              </p>
              <p id="notes-count" className="text-sm text-muted-foreground">
                {formData.notes.length}/500 characters
              </p>
            </div>
            {formErrors.notes && (
              <FieldError id="notes-error" message={formErrors.notes} />
            )}
          </div>
        </div>
      </Card>

      {/* Line Items Section */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Items to Write Off</h3>
              <p className="text-sm text-muted-foreground">
                Select items to remove from inventory
              </p>
            </div>
            <Button onClick={handleAddLineItem} variant="secondary">
              <Plus className="h-4 w-4 mr-2" />
              Add Item to Write Off
            </Button>
          </div>

          {/* Table or Empty State */}
          {lineItems.length === 0 || !lineItems.some(item => item.stockCode) ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No items added yet</p>
              <Button onClick={handleAddLineItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item to Write Off
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30%]">Stock Item</TableHead>
                    <TableHead className="w-[18%]">Location</TableHead>
                    <TableHead className="w-[12%]">Quantity</TableHead>
                    <TableHead className="w-[15%]">Unit Price</TableHead>
                    <TableHead className="w-[18%]">Line Total</TableHead>
                    <TableHead className="w-[7%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item) => (
                    <TableRow key={item.id} data-line-id={item.id}>
                      {/* Stock Item */}
                      <TableCell>
                        <div className="space-y-1">
                          <Select
                            value={item.stockCode}
                            onValueChange={(value) =>
                              handleLineItemChange(item.id, "stockCode", value)
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                item.errors.stockCode && "border-destructive"
                              )}
                              aria-required="true"
                              aria-invalid={!!item.errors.stockCode}
                            >
                              <SelectValue placeholder="Select stock item" />
                            </SelectTrigger>
                            <SelectContent>
                              {mockStockItems.map((stock) => (
                                <SelectItem key={stock.code} value={stock.code}>
                                  {stock.code} - {stock.description}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {item.errors.stockCode && (
                            <FieldError message={item.errors.stockCode} />
                          )}
                        </div>
                      </TableCell>

                      {/* Location */}
                      <TableCell>
                        <div className="space-y-1">
                          <Select
                            value={item.location}
                            onValueChange={(value) =>
                              handleLineItemChange(item.id, "location", value)
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                item.errors.location && "border-destructive"
                              )}
                              aria-required="true"
                              aria-invalid={!!item.errors.location}
                            >
                              <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent>
                              {locations.map((location) => (
                                <SelectItem key={location} value={location}>
                                  {location}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {item.errors.location && (
                            <FieldError message={item.errors.location} />
                          )}
                        </div>
                      </TableCell>

                      {/* Quantity */}
                      <TableCell>
                        <div className="space-y-1">
                          <Input
                            type="number"
                            placeholder="0"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(item.id, "quantity", e.target.value)
                            }
                            className={cn(
                              "w-full",
                              item.errors.quantity && "border-destructive"
                            )}
                            aria-required="true"
                            aria-invalid={!!item.errors.quantity}
                          />
                          {item.errors.quantity && (
                            <FieldError message={item.errors.quantity} />
                          )}
                        </div>
                      </TableCell>

                      {/* Unit Price */}
                      <TableCell>
                        <div className="space-y-1">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                              £
                            </span>
                            <Input
                              type="number"
                              placeholder="0.00"
                              min="0.01"
                              step="0.01"
                              value={item.unitPrice}
                              onChange={(e) =>
                                handleLineItemChange(item.id, "unitPrice", e.target.value)
                              }
                              className={cn(
                                "w-full pl-7",
                                item.errors.unitPrice && "border-destructive"
                              )}
                              aria-required="true"
                              aria-invalid={!!item.errors.unitPrice}
                            />
                          </div>
                          {item.errors.unitPrice && (
                            <FieldError message={item.errors.unitPrice} />
                          )}
                        </div>
                      </TableCell>

                      {/* Line Total */}
                      <TableCell>
                        <div className="font-medium">{formatCurrency(item.lineTotal)}</div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveLineItem(item.id)}
                          className="hover:text-destructive"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {/* Movement Summary */}
      <Card className="p-6 lg:sticky lg:top-4 border-destructive/50">
        <h3 className="text-lg font-bold mb-4">Movement Summary</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Items Written Off</span>
            <span className="text-2xl font-bold">{summary.totalItems}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Quantity Written Off</span>
            <span className="text-2xl font-bold">
              {summary.totalQuantity.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-sm font-medium text-destructive">Total Value Lost</span>
            <span className="text-3xl font-bold text-destructive">
              {formatCurrency(summary.totalValue)}
            </span>
          </div>
        </div>
      </Card>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80 border-t z-50">
        <div className="container max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} variant="destructive">
              {isSaving ? "Saving..." : "Save Write-off"}
            </Button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Write-off?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigateTo("stock-movements")}
              className="bg-destructive hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* High-Value Confirmation Dialog */}
      <AlertDialog open={showHighValueDialog} onOpenChange={setShowHighValueDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              High-Value Write-off
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are writing off <span className="font-bold text-destructive">{formatCurrency(summary.totalValue)}</span> worth of stock. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performSave}
              className="bg-destructive hover:bg-destructive/90"
            >
              Confirm Write-off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
